import { useContext, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import styled from "styled-components";

import { WithTooltip } from "../StyledComponents/WithTooltip";
import { useSubCategories } from "../../hooks/apis/useSubCategories";
import { KBContext } from "../../Context";
import { SubCategoriesLoader } from "../Loaders/SubCategories";
import getStarted from "../../images/getStarted.svg";

const Image_Base_Url = process.env.REACT_APP_IMAGE_BASE_URL;
const DEFAULT_ICON = "category";

function LegacyIconImage({ src, color }) {
  const [error, setError] = useState(false);
  if (error) {
    return (
      <span style={{ fontSize: "40px", height: "40px", color }} className="material-icons-outlined">
        {DEFAULT_ICON}
      </span>
    );
  }
  return (
    <img style={{ width: "40px", height: "40px" }} alt="categoryIcon" src={src} onError={() => setError(true)} />
  );
}

function legacyGetCategoryIcon({ icon, color }) {
  if (!icon) {
    return (
      <span style={{ fontSize: "40px", height: "40px", color }} className="material-icons-outlined">
        {DEFAULT_ICON}
      </span>
    );
  }
  if (icon === "getting-started-icon") {
    return (
      <img style={{ width: "40px", height: "40px" }} alt="categoryIcon" src={getStarted} />
    );
  }
  if (icon.includes("media")) {
    const base = Image_Base_Url?.trim?.() || "";
    if (!base) {
      return (
        <span style={{ fontSize: "40px", height: "40px", color }} className="material-icons-outlined">
          {DEFAULT_ICON}
        </span>
      );
    }
    const sep = base.endsWith("/") ? "" : "/";
    const iconPath = icon.startsWith("/") ? icon.slice(1) : icon;
    return <LegacyIconImage src={`${base}${sep}${iconPath}`} color={color} />;
  }
  return (
    <span style={{ fontSize: "40px", height: "40px", color }} className="material-icons-outlined">
      {icon}
    </span>
  );
}

// ── Old CategoryMeta (no icons, centered, pipe separator) ──
const MetaData = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  margin-top: auto;
`;

const MetaContent = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  color: #8F97B2;
  padding: 0 12px;
  position: relative;
  &:first-child {
    padding-left: 0;
  }
  &:last-child {
    padding-right: 0;
  }
  &:not(:first-child)::before {
    content: '|';
    color: #ECEFF6;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const metaFields = [
  { key: 'immediateSubcategoryCount', getLabel: (count) => count === 1 ? 'category' : 'categories' },
  { key: 'immediateArticleCount', getLabel: (count) => count === 1 ? 'article' : 'articles' },
];

function LegacyCategoryMeta({ categoryData }) {
  return (
    <MetaData>
      {metaFields.map(({ key, getLabel }) => {
        const value = categoryData[key];
        if (!value) return null;
        return (
          <MetaContent key={key}>
            {value} {getLabel(value)}
          </MetaContent>
        );
      })}
    </MetaData>
  );
}

// ── Old SubCategories styles (3-col grid, #CDE2FC border) ──
const Wrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media screen and (max-width: 1400px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 850px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const SubCategory = styled(Link)`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  column-gap: 16px;
  min-width: 0;
  flex-basis: 0;
  flex-grow: 1;
  border-radius: 12px;
  border: 1px solid #CDE2FC;
  padding: 16px 20px;
`;

const Content = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 4px;
  min-width: 0;
  flex-basis: 0;
  flex-grow: 1;
`;

const Title = styled.h3`
  color: #222;
  font-size: 16px;
  font-weight: 500;
  line-height: 28px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`;

// ── Old SubCategories component (no description field) ──
export const LegacySubCategories = ({ selectedCategory, ...props }) => {
  const categorySlug = selectedCategory?.slug;
  const { data: { brandColor } } = useContext(KBContext);
  const { search } = useLocation();

  const { data: subCategories = [], isLoading } = useSubCategories({ categorySlug });

  if (isLoading) {
    return <SubCategoriesLoader count={selectedCategory?.immediateSubcategoryCount ?? 4} {...props} />;
  }

  if (!subCategories?.length) {
    return null;
  }

  return (
    <Wrapper {...props}>
      {subCategories.map((subCategory) => (
        <SubCategory
          style={{ textDecoration: "none" }}
          key={subCategory.categoryId}
          to={`/${subCategory.slug}` + search}
        >
          {legacyGetCategoryIcon({ icon: subCategory.icon, color: brandColor })}
          <Content>
            <WithTooltip isNeeded={subCategory.title.length > 14} content={subCategory.title} size="200px">
              <Title>{subCategory.title}</Title>
            </WithTooltip>
            <LegacyCategoryMeta categoryData={subCategory} />
          </Content>
        </SubCategory>
      ))}
    </Wrapper>
  );
};
